import { addDays, fromParts, parts, startOfDay } from "./time.js";
import { chars, isLetter, isNumber, splitOmitting, isWhitespace, trimSet } from "./text.js";

// `NSDataDetector` has no counterpart off Apple's platforms. This finds what it finds in a coach's
// headings (`22 Sep`, `Sept 22`, `Tue 22/09`, `Lun 30 mars`, `5pm`, `in 3 days`), and, as there,
// only the first thing it finds counts: a lone `Monday` first hides a date after it.

interface Found {
  start: number;
  end: number;
  month: number | null;
  day: number | null;
  year: number | null;
  /** `Friday 3`: the 3rd of a month near today that is a Friday. */
  weekday?: number | null;
  /** A time of day or a relative day: the detector dates it from today. */
  relative?: number;
  absolute?: number;
}

export interface DateMatch {
  start: number;
  end: number;
  text: string;
  month: number;
  day: number;
  year: number | null;
  relativeDays: number | null;
  /** Read off the text itself rather than dated from today. */
  explicit: boolean;
}

const monthWords: [string, number][] = [
  ["january", 1], ["janvier", 1], ["janv", 1], ["jan", 1],
  ["february", 2], ["février", 2], ["fevrier", 2], ["févr", 2], ["fevr", 2], ["feb", 2], ["fév", 2], ["fev", 2],
  ["march", 3], ["mars", 3], ["mar", 3],
  ["april", 4], ["avril", 4], ["apr", 4], ["avr", 4],
  ["may", 5], ["mai", 5],
  ["june", 6], ["juin", 6], ["jun", 6],
  ["july", 7], ["juillet", 7], ["juil", 7], ["jul", 7],
  ["august", 8], ["août", 8], ["aout", 8], ["aug", 8],
  ["september", 9], ["septembre", 9], ["sept", 9], ["sep", 9],
  ["october", 10], ["octobre", 10], ["oct", 10],
  ["november", 11], ["novembre", 11], ["nov", 11],
  ["december", 12], ["décembre", 12], ["decembre", 12], ["déc", 12], ["dec", 12],
];
const months = new Map(monthWords);
const monthPattern = monthWords.map(([word]) => word).sort((a, b) => b.length - a.length).join("|");

const fullWeekdays: [string, number][] = [
  ["sunday", 1], ["monday", 2], ["tuesday", 3], ["wednesday", 4], ["thursday", 5], ["friday", 6], ["saturday", 7],
  ["dimanche", 1], ["lundi", 2], ["mardi", 3], ["mercredi", 4], ["jeudi", 5], ["vendredi", 6], ["samedi", 7],
];
const shortWeekdays: [string, number][] = [
  ["sun", 1], ["mon", 2], ["tue", 3], ["tues", 3], ["wed", 4], ["weds", 4], ["thu", 5], ["thur", 5], ["thurs", 5],
  ["fri", 6], ["sat", 7], ["dim", 1], ["lun", 2], ["mar", 3], ["mer", 4], ["jeu", 5], ["ven", 6], ["sam", 7],
];
const anyWeekday = [...fullWeekdays, ...shortWeekdays].map(([w]) => w).sort((a, b) => b.length - a.length).join("|");
/** A weekday the detector reads before a bare day number (`Friday 3`), and alone (`Fri`). */
const countedWeekday = [...fullWeekdays.map(([w]) => w), "weds", "thurs", "thur", "thu", "fri"]
  .sort((a, b) => b.length - a.length).join("|");

const weekdayOf = new Map([...fullWeekdays, ...shortWeekdays]);

const end = String.raw`(?![\p{L}\p{N}])`;
const ordinal = String.raw`(?:st|nd|rd|th)?`;
const prefix = String.raw`(?:(?:${anyWeekday})[.,]?\s+)?`;

const untilWords = ["until", "till", "through", "before", "ending", "due", "jusqu'au", "avant le"];
const fromWords = ["from", "after", "starting", "beginning"];
const preposition = String.raw`(?:(?<pre>${[...untilWords, ...fromWords].join("|")})\s+)?`;

/** `until Oct 22` is a range from today: the detector dates it today. */
function opened(m: RegExpExecArray): Pick<Found, "relative"> {
  const word = m.groups?.pre?.toLowerCase();
  return word !== undefined && untilWords.includes(word) ? { relative: 0 } : {};
}

const dayRange = String.raw`(?:\s*[-–]\s*\d{1,2}${ordinal})?`;

const patterns: { regex: RegExp; read: (m: RegExpExecArray) => Omit<Found, "start" | "end"> | null }[] = [
  {
    regex: new RegExp(
      String.raw`${preposition}${prefix}(?<d>\d{1,2})${ordinal}${dayRange}\s+(?:of\s+)?(?<mon>${monthPattern})\.?(?:\s+(?<y>\d{4}|\d{2}))?${end}(?:\s*(?:-|–|to)\s*\d{1,2}${ordinal}\s+(?:of\s+)?(?:${monthPattern})\.?${end})?`,
      "iuy",
    ),
    read: (m) => {
      const g = m.groups!;
      const word = g.mon!.toLowerCase();
      if (word === "may") return null;
      return { month: months.get(word)!, day: Number(g.d), year: g.y?.length === 4 ? Number(g.y) : null, ...opened(m) };
    },
  },
  {
    regex: new RegExp(
      String.raw`${preposition}${prefix}(?<mon>${monthPattern})\.?\s+(?<d>\d{1,2})${ordinal}${end}(?:,?\s+(?<y>\d{4})${end})?(?:\s*[-–]\s*(?:(?:${monthPattern})\.?\s+)?\d{1,2}${ordinal}${end})?`,
      "iuy",
    ),
    read: (m) => ({
      month: months.get(m.groups!.mon!.toLowerCase())!,
      day: Number(m.groups!.d),
      year: m.groups!.y ? Number(m.groups!.y) : null,
      ...opened(m),
    }),
  },
  {
    regex: new RegExp(String.raw`${prefix}(\d{1,2})/(\d{1,2})(?:/(\d{4}|\d{2}))?(?![\p{N}/])`, "iuy"),
    read: (m) => {
      const a = m[1]!, b = m[2]!;
      if (a.length < 2 && b.length < 2) return null;
      const x = Number(a), y = Number(b);
      const year = m[3]?.length === 4 ? Number(m[3]) : null;
      if (x >= 1 && x <= 12 && y >= 1 && y <= 31) return { month: x, day: y, year };
      if (y >= 1 && y <= 12 && x >= 1 && x <= 31) return { month: y, day: x, year };
      return null;
    },
  },
  {
    regex: new RegExp(
      String.raw`${preposition}(?:(?<counted>${countedWeekday})\s+(?<cd>\d{1,2})${ordinal}|(?<named>${anyWeekday})\s+(?<nd>\d{1,2})(?:st|nd|rd|th))${end}`,
      "iuy",
    ),
    read: (m) => {
      const value = Number(m.groups!.cd ?? m.groups!.nd);
      const word = (m.groups?.counted ?? m.groups?.named ?? "").toLowerCase();
      const weekday = weekdayOf.get(word) ?? null;
      return value >= 1 && value <= 31 ? { month: null, day: value, year: null, weekday, ...opened(m) } : null;
    },
  },
  {
    regex: new RegExp(String.raw`\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:?\d{2})?${end}`, "iuy"),
    read: (m) => {
      const absolute = Date.parse(m[0]);
      return Number.isNaN(absolute) ? null : { month: null, day: null, year: null, absolute };
    },
  },
  {
    regex: new RegExp(String.raw`(?:\d{4}-\d{2}-\d{2}|\d{1,2}\.\d{1,2}\.\d{4}|\d{1,2}:\d{2})${end}`, "iuy"),
    read: () => ({ month: null, day: null, year: null, relative: 0 }),
  },
  {
    regex: new RegExp(String.raw`${preposition}(?:\d{1,2}(?::\d{2})?\s*(?:am|pm)|\d{1,2}h\d{2}|\d{1,2}\s+h|\d{1,2}\s+o'clock)${end}`, "iuy"),
    read: () => ({ month: null, day: null, year: null, relative: 0 }),
  },
  {
    regex: new RegExp(String.raw`in\s+(\d{1,3})\s+(days?|weeks?)${end}`, "iuy"),
    read: (m) => ({ month: null, day: null, year: null, relative: Number(m[1]) * (m[2]!.toLowerCase().startsWith("week") ? 7 : 1) }),
  },
  {
    regex: new RegExp(
      String.raw`(?:(?:next|last|this)\s+(?:${anyWeekday})|${countedWeekday}|today|tomorrow|yesterday|tonight|aujourd'hui|demain)${end}`,
      "iuy",
    ),
    read: () => ({ month: null, day: null, year: null, relative: 0 }),
  },
];

function isBoundary(text: string, index: number): boolean {
  if (index === 0) return true;
  const code = text.charCodeAt(index - 1);
  const previous = code >= 0xdc00 && code <= 0xdfff && index >= 2 ? text.slice(index - 2, index) : text[index - 1];
  return !(isLetter(previous) || isNumber(previous) || previous === "/");
}

function detect(text: string): Found | null {
  for (let index = 0; index < text.length; index += 1) {
    const head = text[index];
    if (!(isLetter(head) || isNumber(head)) || !isBoundary(text, index)) continue;
    let best: Found | null = null;
    for (const pattern of patterns) {
      pattern.regex.lastIndex = index;
      const match = pattern.regex.exec(text);
      if (match === null) continue;
      const read = pattern.read(match);
      if (read === null) continue;
      const found = { start: index, end: index + match[0].length, ...read };
      if (best === null || found.end > best.end) best = found;
    }
    if (best !== null) return best;
  }
  return null;
}

/** `firstDateMatch`: a written date carries a number and a month or a slash. */
export function firstDateMatch(heading: string, now: number, timeZone: string): DateMatch | null {
  const found = detect(heading);
  if (found === null) return null;
  const matched = heading.slice(found.start, found.end);
  if (!chars(matched).some(isNumber) || !chars(matched).some((c) => isLetter(c) || c === "/")) return null;
  let month = found.month;
  let day = found.day;
  let relativeDays: number | null = null;
  if (found.relative !== undefined && month !== null) {
    const date = parts(addDays(now, found.relative, timeZone), timeZone);
    month = date.month;
    day = date.day;
    relativeDays = found.relative;
  } else if (month === null) {
    const today = parts(now, timeZone);
    if (found.absolute !== undefined) {
      const date = parts(found.absolute, timeZone);
      month = date.month;
      day = date.day;
    } else if (day === null) {
      relativeDays = found.relative ?? 0;
      const date = parts(addDays(now, relativeDays, timeZone), timeZone);
      month = date.month;
      day = date.day;
    } else if (found.relative !== undefined) {
      const date = parts(addDays(now, found.relative, timeZone), timeZone);
      month = date.month;
      day = date.day;
    } else {
      month = monthWithWeekday(day, found.weekday ?? null, today);
    }
  }
  const explicit = found.month !== null && relativeDays === null;
  return { start: found.start, end: found.end, text: matched, month, day: day!, year: found.year, relativeDays, explicit };
}

/** Within two months ahead, then two back, the nearest month whose `day` falls on the weekday. */
function monthWithWeekday(day: number, weekday: number | null, today: { year: number; month: number; day: number }): number {
  const fits = (offset: number): boolean => {
    const date = new Date(Date.UTC(today.year, today.month - 1 + offset, day));
    return date.getUTCDate() === day && (weekday === null || date.getUTCDay() + 1 === weekday);
  };
  const ahead = day >= today.day ? [0, 1, 2] : [1, 2];
  const back = day >= today.day ? [-1, -2] : [0, -1, -2];
  for (const offset of [...ahead, ...back]) {
    if (fits(offset)) return ((today.month - 1 + offset + 12) % 12) + 1;
  }
  return day >= today.day ? today.month : (today.month % 12) + 1;
}

export const weekdays: Record<string, number> = {
  sunday: 1, sun: 1,
  monday: 2, mon: 2,
  tuesday: 3, tue: 3, tues: 3,
  wednesday: 4, wed: 4, weds: 4,
  thursday: 5, thu: 5, thur: 5, thurs: 5,
  friday: 6, fri: 6,
  saturday: 7, sat: 7,
};

export interface HeadingDay {
  date: number;
  remainder: string;
}

function hasFourDigitRun(text: string): boolean {
  let run = 0;
  for (const c of text) {
    run = isNumber(c) ? run + 1 : 0;
    if (run === 4) return true;
  }
  return false;
}

function resolve(match: DateMatch, floor: number, timeZone: string): number {
  const spelledOut = hasFourDigitRun(match.text);
  const year = spelledOut && match.year !== null ? match.year : parts(floor, timeZone).year;
  const result = fromParts(timeZone, year, match.month, match.day);
  const halfAYearBack = addDays(floor, -180, timeZone);
  if (spelledOut || !(result < halfAYearBack)) return result;
  return fromParts(timeZone, year + 1, match.month, match.day);
}

function next(weekday: number, floor: number, timeZone: string): number {
  const today = parts(floor, timeZone).weekday;
  return addDays(floor, (weekday - today + 7) % 7, timeZone);
}

function stripDayWords(text: string): string {
  return splitOmitting(text, isWhitespace)
    .filter((token) => {
      const word = chars(token.toLowerCase()).filter(isLetter).join("");
      return weekdays[word] === undefined && word !== "today" && word !== "tomorrow";
    })
    .join(" ");
}

function tidy(text: string): string {
  return trimSet(text, " \t-–—:;,.|·•");
}

/** `RuleParser.day(in:onOrAfter:now:calendar:)`. */
export function dayIn(heading: string, floorDate: number, now: number, timeZone: string): HeadingDay | null {
  const from = startOfDay(floorDate, timeZone);
  const today = startOfDay(now, timeZone);

  const match = firstDateMatch(heading, now, timeZone);
  if (match !== null) {
    const withoutDate = heading.slice(0, match.start) + " " + heading.slice(match.end);
    return { date: resolve(match, from, timeZone), remainder: tidy(stripDayWords(withoutDate)) };
  }

  for (const word of splitOmitting(heading.toLowerCase(), (c) => !isLetter(c))) {
    let date: number | null = null;
    if (word === "today") date = today;
    else if (word === "tomorrow") date = addDays(today, 1, timeZone);
    else if (weekdays[word] !== undefined) date = next(weekdays[word]!, from, timeZone);
    if (date !== null) return { date, remainder: tidy(stripDayWords(heading)) };
  }
  return null;
}

export function isDateHeading(heading: string, now: number, timeZone: string): boolean {
  return firstDateMatch(heading, now, timeZone) !== null;
}
